import React from 'react';
import 'dayjs/locale/es';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import dayjs from 'dayjs';
import { createColumnHelper } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import { Card, DotIcon, formatCurrency, Table } from '@avila-tek/ui/src';
import { BenefitRequest } from '../../../models';
import { useUser } from '../../../hooks';
import ReceivableFilters from '../ReceivableTable/ReceivableFilters';

dayjs.extend(localizedFormat);
dayjs.locale('es');

const columnHelper = createColumnHelper<BenefitRequest>();

export const translateBenefitStatusRequest = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  declined: 'Rechazado',
  cancelled: 'Cancelado',
};

const columns = [
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore library has a issue: https://github.com/TanStack/table/issues/4224
  columnHelper.accessor('user', {
    header: 'Nombre',
    cell: (info) =>
      `${info?.getValue()?.firstName} ${info?.getValue()?.lastName}`,
  }),

  columnHelper.accessor('amountRequested', {
    header: 'Monto solicitado',
    cell: (info) => `$${formatCurrency(info.getValue()) ?? 0}`,
  }),

  columnHelper.accessor('amountApproved', {
    header: 'Monto aprobado',
    cell: (info) => `$${formatCurrency(info.getValue()) ?? 0}`,
  }),

  columnHelper.accessor('createdAt', {
    header: 'Fecha',
    cell: (info) => `${dayjs(info.row.original.createdAt).format('L')}`,
  }),

  columnHelper.accessor('paid', {
    header: 'Pagado',
    cell: (info) => (
      <p className="flex gap-2 items-center">
        <DotIcon
          className={` ${
            info.getValue() ? 'text-success-300' : ' text-danger-200'
          } w-5 h-5`}
        />

        {info.getValue() && info.row.original.status === 'approved' && 'Pagado'}
        {!info.getValue() &&
          info.row.original.status === 'approved' &&
          'Pendiente'}
        {info.row.original.status !== 'approved' && 'N/A'}
      </p>
    ),
  }),

  columnHelper.accessor('status', {
    header: 'Estatus',
    cell: (info) => (
      <p className="flex gap-2 items-center">
        <DotIcon
          className={` ${
            info.getValue() === 'approved' && info.row.original.active
              ? 'text-success-300'
              : ' text-danger-200'
          } w-5 h-5`}
        />
        {translateBenefitStatusRequest[info.getValue()]}
      </p>
    ),
  }),
];

interface RequestTableProps {
  data: BenefitRequest[];
  setPaidFilter: React.Dispatch<React.SetStateAction<boolean>>;
  setStatusFilter: React.Dispatch<React.SetStateAction<string>>;
  paidFilter: boolean;
  statusFilter: string;
}

function RequestTable({
  data,
  setPaidFilter,
  setStatusFilter,
  paidFilter,
  statusFilter,
}: RequestTableProps) {
  const router = useRouter();
  const [user] = useUser();

  const onClickRow = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    e.preventDefault();
    const { id } = e.currentTarget.dataset;
    const path =
      user.userType === 'admin'
        ? `/app/receivable/${router.query.companyId}/${id}`
        : `/app/requests/${id}`;

    router.push(path);
  };

  return (
    <Card className="overflow-x-auto p-5">
      <Table<BenefitRequest>
        data={data}
        columns={columns}
        href="/app/maintenance/sales/create"
        manualPagination={false}
      >
        <Table.Header>
          <Table.SearchInput />
          <ReceivableFilters
            setPaidFilter={setPaidFilter}
            setStatusFilter={setStatusFilter}
            paidFilter={paidFilter}
            statusFilter={statusFilter}
          />
        </Table.Header>
        <Table.Content<BenefitRequest> onClickRow={onClickRow} />
        <Table.Footer>
          <Table.Pagination className="ml-auto" />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default RequestTable;
